import React from 'react';
import type { GiftProgressState } from '../../offer.types';
import GiftCard from './GiftCard';

interface GiftSectionProps {
  states: GiftProgressState[];
  onApplyCoupon?: (couponCode: string) => Promise<void> | void;
  loading?: boolean;
  /** When true, renders the compact stacked variant for the OrderSummaryCard. */
  compact?: boolean;
  /**
   * Hide cards that don't have any progress yet (triggerType=value with min=0,
   * or quantity with min=0). Defaults to false — renders everything.
   */
  hideEmpty?: boolean;
  /** Only render gifts whose status matches one of the provided values. */
  statusFilter?: GiftProgressState['status'][];
}

const GiftSection: React.FC<GiftSectionProps> = ({
  states,
  onApplyCoupon,
  loading,
  compact = false,
  hideEmpty = false,
  statusFilter,
}) => {
  if (!states || states.length === 0) return null;

  const visible = states.filter((s) => {
    if (statusFilter && !statusFilter.includes(s.status)) return false;
    if (!hideEmpty) return true;
    if (s.gift.triggerType === 'always') return true;
    return s.progress > 0;
  });

  if (visible.length === 0) return null;

  return (
    <div
      className={`rounded-lg border border-pink-200 bg-pink-50/40 dark:border-pink-900/40 dark:bg-pink-950/20 ${
        compact ? 'p-2.5' : 'p-3'
      }`}
    >
      <div className="mb-2 flex items-center gap-1.5 px-0.5">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-pink-600 text-white">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 12 20 22 4 22 4 12" />
            <rect x="2" y="7" width="20" height="5" />
            <line x1="12" y1="22" x2="12" y2="7" />
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
          </svg>
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-pink-700 dark:text-pink-300">
          {visible.length === 1 ? 'Brinde' : `Brindes (${visible.length})`}
        </span>
      </div>

      <div className="space-y-2">
        {visible.map((state) => (
          <GiftCard
            key={state.gift.nanoId}
            state={state}
            onApplyCoupon={onApplyCoupon}
            loading={loading}
            compact={compact}
          />
        ))}
      </div>
    </div>
  );
};

export default GiftSection;
